import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import RequiredVsActual from "@/components/RequiredVsActual";
import AllocationBadge from "@/components/AllocationBadge";
import TwoLineDocNo from "@/components/TwoLineDocNo";
import { RowActions } from "@/components/RowActions";
import {
  ArrowClockwiseIcon, MagnifyingGlassIcon, CheckCircleIcon,
  XCircleIcon, ClipboardTextIcon, WarningCircleIcon, InfoIcon,
  ShoppingCartIcon, ArrowRightIcon, PencilSimpleIcon, PrinterIcon,
  ProhibitIcon,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import { getToken } from "@/lib/secureStorage";

// Feb 2026 · Purchase Requirements — list every PR auto-generated by
// MRP Process, gated by the Approval Matrix (doc_type=PR). Approver
// role is derived from PR value banding. Falls back to Administrator
// when the matrix is empty.

const fmt = (n, d = 2) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: d, minimumFractionDigits: d });
const STATUS_STYLE = {
  OPEN: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  PENDING: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  APPROVED: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  REJECTED: "bg-red-500/15 text-red-300 border-red-500/40",
  CANCELLED: "bg-zinc-500/15 text-zinc-300 border-zinc-500/40 [border-style:dashed]",
  CONVERTED: "bg-blue-500/15 text-blue-300 border-blue-500/40",
};
const btnPrimary = "inline-flex items-center gap-1.5 h-9 px-4 bg-[var(--accent)] text-white text-xs font-mono uppercase tracking-widest rounded-sm hover:opacity-90 transition disabled:opacity-40 disabled:cursor-not-allowed";
const btnGhost = "inline-flex items-center gap-1.5 h-9 px-4 border border-[var(--border)] text-xs font-mono uppercase tracking-widest rounded-sm hover:bg-[var(--bg-2)] transition disabled:opacity-40 disabled:cursor-not-allowed";
const btnDanger = "inline-flex items-center gap-1.5 h-9 px-4 border border-red-500/50 text-red-300 text-xs font-mono uppercase tracking-widest rounded-sm hover:bg-red-500/10 transition disabled:opacity-40 disabled:cursor-not-allowed";

export default function PurchaseRequirements() {
  const { user } = useAuth() || {};
  const [prs, setPrs] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [detail, setDetail] = useState(null);       // hydrated PR
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  // Convert-to-PO
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertPreview, setConvertPreview] = useState(null);
  const [converting, setConverting] = useState(false);
  // Edit mode inside detail dialog
  const [editMode, setEditMode] = useState(false);
  const [editLines, setEditLines] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);

  const openPdf = (pr) => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    const url = `${backend}/api/mrp/prs/${pr.id}/pdf`;
    // Use fetch to attach the JWT then open the blob
    const tok = getToken();
    fetch(url, { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.blob())
      .then(b => window.open(URL.createObjectURL(b), "_blank"))
      .catch(() => toast.error("Failed to open PDF"));
  };

  const startEdit = () => {
    if (!detail) return;
    setEditLines((detail.lines || []).map(l => ({
      rm_id: l.rm_id,
      qty_required: l.qty_required ?? l.qty ?? 0,
      qty_adjustment: l.qty_adjustment ?? 0,
      adjustment_notes: l.adjustment_notes ?? "",
    })));
    setEditMode(true);
  };

  const cancelEdit = () => { setEditMode(false); setEditLines([]); };

  const saveEdit = async () => {
    if (!detail) return;
    setSavingEdit(true);
    try {
      const payload = { lines: editLines.map(l => ({
        rm_id: l.rm_id,
        qty_adjustment: Number(l.qty_adjustment) || 0,
        adjustment_notes: l.adjustment_notes || "",
      })) };
      const { data } = await api.put(`/mrp/prs/${detail.id}`, payload);
      toast.success(`${data.pr_no} updated`);
      setDetail(data); setEditMode(false); setEditLines([]);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingEdit(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/mrp/prs/list");
      setPrs(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Purchase Requirements");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openDetail = async (row) => {
    try {
      const { data } = await api.get(`/mrp/prs/${row.id}`);
      setDetail(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load PR detail");
    }
  };

  const [selectedIds, setSelectedIds] = useState(new Set());
  const filtered = useMemo(() => {
    let list = prs;
    if (statusFilter !== "ALL") list = list.filter(p => p.status === statusFilter);
    const n = q.trim().toLowerCase();
    if (!n) return list;
    return list.filter(p =>
      (p.pr_no || "").toLowerCase().includes(n) ||
      (p.mrp_no || "").toLowerCase().includes(n) ||
      (p.sales_order_no || "").toLowerCase().includes(n) ||
      (p.work_order_no || "").toLowerCase().includes(n) ||
      (p.customer || "").toLowerCase().includes(n) ||
      (p.product_code || "").toLowerCase().includes(n));
  }, [prs, q, statusFilter]);

  const counts = useMemo(() => {
    const c = { ALL: prs.length, OPEN: 0, APPROVED: 0, REJECTED: 0, CANCELLED: 0, CONVERTED: 0 };
    prs.forEach(p => { c[p.status] = (c[p.status] || 0) + 1; });
    return c;
  }, [prs]);

  const canApprove = (pr) => {
    if (!pr || pr.status !== "OPEN") return false;
    const role = user?.role || "";
    const need = pr.approver_rule?.approver_role || "Administrator";
    return role === "Administrator" || role === need;
  };

  const approve = async () => {
    if (!detail) return;
    setActing(true);
    try {
      const { data } = await api.post(`/mrp/prs/${detail.id}/approve`);
      toast.success(`${data.pr_no} approved`);
      setDetail(null); await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally { setActing(false); }
  };

  const reject = async () => {
    if (!detail) return;
    setActing(true);
    try {
      const { data } = await api.post(`/mrp/prs/${detail.id}/reject`, { reason: rejectReason });
      toast.success(`${data.pr_no} rejected`);
      setRejectOpen(false); setRejectReason(""); setDetail(null); await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally { setActing(false); }
  };

  const openConvert = async () => {
    if (!detail) return;
    try {
      const { data } = await api.get(`/mrp/prs/${detail.id}/convert-preview`);
      setConvertPreview(data);
      setConvertOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to preview PO grouping");
    }
  };

  const confirmConvert = async () => {
    if (!detail) return;
    setConverting(true);
    try {
      const { data } = await api.post(`/mrp/prs/${detail.id}/convert-to-po`);
      const pos = data.pos || [];
      toast.success(`${data.pr_no} converted → ${pos.length} PO${pos.length !== 1 ? "s" : ""} · ${pos.map(p => p.po_no).join(", ")}`);
      setConvertOpen(false); setConvertPreview(null); setDetail(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Convert failed");
    } finally { setConverting(false); }
  };

  const StatusTab = ({ code, label }) => (
    <button
      data-testid={`pr-filter-${code.toLowerCase()}`}
      onClick={() => setStatusFilter(code)}
      className={`h-8 px-3 text-xs font-mono uppercase tracking-widest rounded-sm border transition ${
        statusFilter === code
          ? "border-[var(--accent)] text-white bg-[var(--accent)]/20"
          : "border-[var(--border)] text-zinc-400 hover:bg-[var(--bg-2)]"
      }`}
    >
      {label} · {counts[code] || 0}
    </button>
  );

  return (
    <>
      <div className="p-6 space-y-4" data-testid="pr-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="overline">Purchase</div>
            <h1 className="font-display text-2xl mt-1">Purchase Requirements</h1>
            <div className="text-xs text-zinc-500 mt-1">
              Auto-generated when MRP identifies a shortage. Approver role is
              picked from the Approval Matrix (doc_type=PR) by value band.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="pr-refresh" onClick={load} className={btnGhost} disabled={loading}>
              <ArrowClockwiseIcon size={12} weight="bold" />Refresh
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="tactical-card p-3 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusTab code="ALL" label="All" />
            <StatusTab code="OPEN" label="Open" />
            <StatusTab code="APPROVED" label="Approved" />
            <StatusTab code="CONVERTED" label="Converted" />
            <StatusTab code="REJECTED" label="Rejected" />
            <StatusTab code="CANCELLED" label="Cancelled" />
          </div>
          <div className="relative">
            <MagnifyingGlassIcon size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="pr-search"
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search PR / MRP / customer / product…"
              className="h-8 w-80 pl-7 pr-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs focus:outline-none focus:border-[var(--accent)]"
            />
          </div>
        </div>

        {/* Feb 2026 · Bulk PO Consolidation strip — appears the moment
            any APPROVED PR is checked so users always discover the
            feature; the action button stays disabled with a helpful
            tooltip until 2+ APPROVED PRs sharing the same SO are picked. */}
        {selectedIds.size >= 1 && (() => {
          const picked = filtered.filter(p => selectedIds.has(p.id));
          const soSet = new Set(picked.map(p => p.sales_order_id || ""));
          const allApproved = picked.every(p => p.status === "APPROVED");
          const sameSo = soSet.size === 1 && [...soSet][0];
          const enough = picked.length >= 2;
          const canBulk = enough && allApproved && sameSo;
          const hint = !enough ? `Select at least ${2 - picked.length} more APPROVED PR sharing the same SO/ESO`
            : !allApproved ? "Only APPROVED PRs can be consolidated"
            : !sameSo ? "All selected PRs must share the same SO/ESO"
            : "Ready — one Draft PO per supplier will be created";
          return (
            <div className="tactical-card p-3 flex items-center gap-3 border-amber-500/40 bg-amber-500/5" data-testid="pr-bulk-strip">
              <div className="text-xs">
                <div className="overline">Bulk Consolidate</div>
                <div className="font-mono text-white">{picked.length} PR{picked.length === 1 ? "" : "s"} selected{sameSo ? ` · ${picked[0].sales_order_no || "—"}` : ""}</div>
                <div className={`text-[10px] mt-0.5 ${canBulk ? "text-emerald-300" : "text-amber-300"}`}>{hint}</div>
              </div>
              <button onClick={() => setSelectedIds(new Set())} className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-white ml-auto">Clear</button>
              <button
                data-testid="pr-bulk-consolidate-btn"
                disabled={!canBulk}
                title={hint}
                onClick={async () => {
                  if (!window.confirm(`Consolidate ${picked.length} PRs into supplier-grouped Draft POs?\n\nIdentical raw materials will be summed into one line. All source PRs will be marked CONVERTED.`)) return;
                  try {
                    const { data } = await api.post("/mrp/prs/bulk-consolidate-po", { pr_ids: picked.map(p => p.id) });
                    toast.success(`${data.po_count} PO${data.po_count > 1 ? "s" : ""} created · SO ${data.sales_order_no || ""}`);
                    setSelectedIds(new Set());
                    await load();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Consolidation failed");
                  }
                }}
                className="h-9 px-4 rounded-sm bg-amber-500 hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed text-black text-xs font-semibold uppercase tracking-widest flex items-center gap-1.5"
              >
                <ShoppingCartIcon size={13} weight="bold" />Consolidate → Draft PO
              </button>
            </div>
          );
        })()}

        {/* Table */}
        <div className="tactical-card p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px] sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-center w-8">
                    <input type="checkbox" data-testid="pr-select-all"
                      checked={filtered.length > 0 && filtered.every(p => selectedIds.has(p.id) || p.status !== "APPROVED")}
                      onChange={e => {
                        const next = new Set(selectedIds);
                        filtered.forEach(p => { if (p.status === "APPROVED") { e.target.checked ? next.add(p.id) : next.delete(p.id); } });
                        setSelectedIds(next);
                      }} />
                  </th>
                  <th className="px-3 py-2 text-left">PR No</th>
                  <th className="px-3 py-2 text-left">MRP</th>
                  <th className="px-3 py-2 text-left">SO / ESO</th>
                  <th className="px-3 py-2 text-left">Work Order</th>
                  <th className="px-3 py-2 text-left">Customer</th>
                  <th className="px-3 py-2 text-left">Product</th>
                  <th className="px-3 py-2 text-right w-14">Lines</th>
                  <th className="px-3 py-2 text-right w-28">Value (₹)</th>
                  <th className="px-3 py-2 text-center w-24">Status</th>
                  <th className="px-3 py-2 text-right w-24">Date</th>
                  <th className="px-3 py-2 text-center w-24">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={12} className="text-center py-10 text-xs text-zinc-500">
                    {prs.length ? "No matches for the current filter" : "No Purchase Requirements yet — run MRP → Process to generate one"}
                  </td></tr>
                )}
                {filtered.map((p, i) => (
                  <tr
                    key={p.id}
                    data-testid={`pr-row-${i}`}
                    onClick={() => openDetail(p)}
                    className="border-t border-[var(--border)] cursor-pointer hover:bg-[var(--bg-2)]/40 transition"
                  >
                    <td className="px-3 py-2 text-center" onClick={e => e.stopPropagation()}>
                      {p.status === "APPROVED" && (
                        <input type="checkbox" data-testid={`pr-select-${i}`}
                          checked={selectedIds.has(p.id)}
                          onChange={e => {
                            const next = new Set(selectedIds);
                            e.target.checked ? next.add(p.id) : next.delete(p.id);
                            setSelectedIds(next);
                          }} />
                      )}
                      {/* Feb 2026 · Authorization Gate visibility — Open
                          PRs are hidden from PO consolidation and MRQ
                          issuing until authorised. Stamp a small chip
                          so buyers see the pending status at a glance. */}
                      {p.status === "OPEN" && (
                        <span
                          data-testid={`pr-waiting-auth-${p.pr_no}`}
                          title="Waiting for Management authorization"
                          className="ml-1 px-1 py-0.5 rounded-sm bg-amber-500/15 text-amber-400 border border-amber-500/30 text-[9px] font-mono uppercase tracking-widest"
                        >
                          WFA
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-right"><TwoLineDocNo value={p.pr_no} className="text-[var(--accent)]" /></td>
                    <td className="px-3 py-2 font-mono text-[10px] text-zinc-400">{p.mrp_no || "—"}</td>
                    <td className="px-3 py-2 font-mono text-[11px]">
                      {p.sales_order_no ? (
                        <span>
                          <span className={`text-[9px] px-1.5 py-0.5 rounded-full mr-1.5 border font-mono uppercase ${p.sales_order_type === "ESO" ? "bg-blue-500/15 text-blue-300 border-blue-500/40" : "bg-purple-500/15 text-purple-300 border-purple-500/40"}`}>{p.sales_order_type || "SO"}</span>
                          <span className="text-zinc-300">{p.sales_order_no}</span>
                        </span>
                      ) : <span className="text-zinc-600">—</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-zinc-300">{p.work_order_no || <span className="text-zinc-600">—</span>}</td>
                    <td className="px-3 py-2 text-[11px] text-zinc-400 truncate max-w-[180px]">{p.customer || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs text-purple-300 truncate max-w-[220px]">{p.product_code || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{(p.lines || []).length}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{fmt(p.value)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-[9px] px-2 py-0.5 rounded-full border font-mono uppercase tracking-widest ${STATUS_STYLE[p.status] || "bg-zinc-500/15 text-zinc-300 border-zinc-500/40"}`}>{p.status}</span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-[10px] text-zinc-500">{(p.created_at || "").slice(0, 10)}</td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        {p.status === "CANCELLED" && (
                          <span
                            title={p.cancelled_reason ? `Cancelled · ${p.cancelled_reason}` : "Cancelled"}
                            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border border-red-500/40 text-red-300 bg-red-500/10 font-mono uppercase tracking-widest"
                          >
                            <ProhibitIcon size={9} weight="bold" />Cancelled
                          </span>
                        )}
                        <div onClick={(e) => e.stopPropagation()}>
                          <RowActions
                            testid={`pr-actions-${i}`}
                            items={[
                              { label: "View", testid: `pr-view-${i}`, onClick: () => openDetail(p) },
                              {
                                label: "Cancel PR", tone: "warn", testid: `pr-cancel-${i}`,
                                show: p.status !== "CONVERTED" && p.status !== "CANCELLED",
                                onClick: async () => {
                                  const reason = window.prompt(`Cancel ${p.pr_no}?\n\nEnter a reason (required, min 3 chars):`);
                                  if (reason === null) return;
                                  if (reason.trim().length < 3) { toast.error("Reason must be at least 3 characters"); return; }
                                  try {
                                    await api.post(`/mrp/prs/${p.id}/cancel`, { reason: reason.trim() });
                                    toast.success(`${p.pr_no} · cancelled`);
                                    load();
                                  } catch (err) {
                                    toast.error(err?.response?.data?.detail || "Cancel failed");
                                  }
                                },
                              },
                              { label: "Edit", testid: `pr-edit-${i}`, show: p.status === "OPEN", onClick: () => { openDetail(p).then(() => setTimeout(startEdit, 250)); } },
                              { label: "Print PDF", testid: `pr-pdf-${i}`, onClick: () => openPdf(p) },
                            ]}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Detail + Approve/Reject Dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="pr-detail-dialog" className="bg-[var(--bg-1)] border-[var(--border)] max-w-6xl max-h-[92vh] overflow-y-auto" description="Full detail view of the Purchase Requirement including linked orders.">
          <DialogHeader>
            <DialogTitle className="font-display text-lg flex items-center gap-2">
              {detail?.pr_no}
              {detail && (
                <span className={`text-[9px] px-2 py-0.5 rounded-full border font-mono uppercase tracking-widest ${STATUS_STYLE[detail.status] || ""}`}>{detail.status}</span>
              )}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div><div className="text-zinc-500 mb-0.5">MRP Run</div><div className="font-mono">{detail.mrp_no}</div></div>
                <div>
                  <div className="text-zinc-500 mb-0.5">Sales Order</div>
                  <div className="font-mono flex items-center gap-1.5">
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full border uppercase ${detail.sales_order_type === "ESO" ? "bg-blue-500/15 text-blue-300 border-blue-500/40" : "bg-purple-500/15 text-purple-300 border-purple-500/40"}`}>{detail.sales_order_type || "SO"}</span>
                    <span className="text-zinc-200">{detail.sales_order_no || "—"}</span>
                  </div>
                </div>
                <div><div className="text-zinc-500 mb-0.5">Work Order</div><div className="font-mono text-zinc-200">{detail.work_order_no || "—"}</div></div>
                <div><div className="text-zinc-500 mb-0.5">Customer</div><div className="truncate">{detail.customer || "—"}</div></div>
                <div className="md:col-span-2"><div className="text-zinc-500 mb-0.5">Product</div><div className="font-mono text-purple-300 truncate">{detail.product_code || "—"}</div></div>
                <div><div className="text-zinc-500 mb-0.5">Value (₹)</div><div className="font-mono tabular-nums">{fmt(detail.value)}</div></div>
                <div><div className="text-zinc-500 mb-0.5">Required</div><div className="font-mono text-[10px]">{(detail.required_date || "").slice(0, 10) || "—"}</div></div>
                <div className="md:col-span-4"><div className="text-zinc-500 mb-0.5">Approver Rule</div>
                  <div className="font-mono text-[11px]">
                    {detail.approver_rule?.approver_role || "Administrator"}
                    <span className="text-zinc-500 ml-2">
                      ({detail.approver_rule?.code || "PR-DEFAULT"} · band {fmt(detail.approver_rule?.min_value)} – {(detail.approver_rule?.max_value || 0) === 0 ? "∞" : fmt(detail.approver_rule?.max_value)})
                    </span>
                  </div>
                </div>
              </div>

              {detail.status === "APPROVED" && (
                <div data-testid="pr-approved-banner" className="flex items-center gap-2 text-[11px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/40 rounded-sm px-3 py-2">
                  <CheckCircleIcon size={14} weight="bold" />
                  Approved by <b className="font-mono">{detail.approved_by}</b> ({detail.approver_role}) on {(detail.approved_at || "").slice(0, 19).replace("T", " ")} · rule {detail.approver_rule_code}
                </div>
              )}
              {detail.status === "REJECTED" && (
                <div data-testid="pr-rejected-banner" className="flex items-start gap-2 text-[11px] text-red-300 bg-red-500/10 border border-red-500/40 rounded-sm px-3 py-2">
                  <XCircleIcon size={14} weight="bold" className="mt-0.5" />
                  <div>
                    Rejected by <b className="font-mono">{detail.rejected_by}</b> on {(detail.rejected_at || "").slice(0, 19).replace("T", " ")}
                    {detail.reject_reason && <div className="mt-1 text-zinc-300">Reason: {detail.reject_reason}</div>}
                  </div>
                </div>
              )}
              {detail.status === "CONVERTED" && (
                <div data-testid="pr-converted-banner" className="flex items-start gap-2 text-[11px] text-blue-300 bg-blue-500/10 border border-blue-500/40 rounded-sm px-3 py-2">
                  <ShoppingCartIcon size={14} weight="bold" className="mt-0.5" />
                  <div className="flex-1">
                    Converted by <b className="font-mono">{detail.converted_by}</b> on {(detail.converted_at || "").slice(0, 19).replace("T", " ")}
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {(detail.po_refs || []).map((r, i) => (
                        <span
                          key={r.id}
                          data-testid={`pr-po-ref-${i}`}
                          className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-200 border border-blue-500/50"
                          title={`${r.supplier_name} · ${r.line_count} lines · ₹${fmt(r.total_value)}`}
                        >
                          <ArrowRightIcon size={9} weight="bold" />{r.po_no}
                          <span className="text-[9px] opacity-75">· {r.supplier_name}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {detail.status === "OPEN" && !canApprove(detail) && (
                <div className="flex items-center gap-2 text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded-sm px-3 py-2">
                  <InfoIcon size={14} weight="bold" />
                  You are logged in as <b className="font-mono">{user?.role}</b>. Only <b className="font-mono">{detail.approver_rule?.approver_role || "Administrator"}</b> can approve this PR at value {fmt(detail.value)}.
                </div>
              )}

              <div className="border border-[var(--border)] rounded-sm overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px] sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left">RM Code</th>
                      <th className="px-3 py-2 text-right w-24">Required</th>
                      <th className="px-3 py-2 text-right w-24">Adjustment</th>
                      <th className="px-3 py-2 text-right w-24">Total</th>
                      <th className="px-3 py-2 text-center w-14">UoM</th>
                      {editMode && <th className="px-3 py-2 text-left">Adjustment Notes</th>}
                      <th className="px-3 py-2 text-right w-20">Rate</th>
                      <th className="px-3 py-2 text-right w-24">Value (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.lines || []).map((ln, i) => {
                      const el = editMode ? editLines[i] : null;
                      const req = Number(ln.qty_required ?? ln.qty ?? 0);
                      const adj = editMode ? Number(el?.qty_adjustment ?? 0) : Number(ln.qty_adjustment ?? 0);
                      const total = editMode ? (req + adj) : Number(ln.qty_total ?? ln.qty ?? 0);
                      const lv = total * Number(ln.unit_cost || 0);
                      return (
                        <tr key={i} data-testid={`pr-line-${i}`} className="border-t border-[var(--border)]">
                          <td className="px-3 py-2 font-mono text-xs">
                            <div className="flex flex-col gap-0.5">
                              <span className="text-[var(--accent)]">{ln.required_rm_code || ln.rm_code || "—"}</span>
                              <span className="text-[9px] font-mono uppercase tracking-widest text-emerald-400/70">
                                {ln.required_rm_id ? "Required" : "Required (legacy)"}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums text-[11px]">{fmt(req, 3)}</td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums">
                            {editMode ? (
                              <input
                                data-testid={`pr-adj-${i}`}
                                type="number" step="0.001"
                                value={el?.qty_adjustment ?? 0}
                                onChange={e => setEditLines(prev => prev.map((x, j) => j === i ? { ...x, qty_adjustment: e.target.value } : x))}
                                className="w-20 h-7 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-right text-xs font-mono focus:outline-none focus:border-[var(--accent)]"
                              />
                            ) : (
                              <span className={adj > 0 ? "text-amber-300" : adj < 0 ? "text-red-300" : "text-zinc-500"}>{adj !== 0 ? (adj > 0 ? "+" : "") + fmt(adj, 3) : "0.000"}</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold text-emerald-300" data-testid={`pr-total-${i}`}>{fmt(total, 3)}</td>
                          <td className="px-3 py-2 text-center text-[10px] text-zinc-500 font-mono">{ln.uom || "kg"}</td>
                          {editMode && (
                            <td className="px-3 py-2 text-[11px]">
                              <input
                                data-testid={`pr-notes-${i}`}
                                type="text" maxLength={140}
                                placeholder="e.g. MOQ 1 bag = 25 kg"
                                value={el?.adjustment_notes ?? ""}
                                onChange={e => setEditLines(prev => prev.map((x, j) => j === i ? { ...x, adjustment_notes: e.target.value } : x))}
                                className="w-full h-7 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs focus:outline-none focus:border-[var(--accent)]"
                              />
                            </td>
                          )}
                          <td className="px-3 py-2 text-right font-mono tabular-nums text-[10px] text-zinc-400">{fmt(ln.unit_cost)}</td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums">{fmt(lv)}</td>
                        </tr>
                      );
                    })}
                    <tr className="border-t-2 border-[var(--border)] bg-[var(--bg-2)]/50">
                      <td colSpan={editMode ? 7 : 6} className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-zinc-500">Total</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums font-bold">{fmt(detail.value)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter className="border-t border-[var(--border)] pt-3">
            <button className={btnGhost} onClick={() => { setDetail(null); cancelEdit(); }}>Close</button>
            {/* Feb 2026 · Admin-only PR Unapprove — flips APPROVED → OPEN so lines can
                be re-edited. Refuses when the PR is already CONVERTED (backend 409). */}
            {detail?.status === "APPROVED" && user?.role === "Administrator" && (
              <button
                data-testid="pr-unapprove-btn"
                onClick={async () => {
                  const reason = window.prompt(`Unapprove ${detail.pr_no}?\n\nEnter a reason (required, kept in the audit log):`);
                  if (!reason || !reason.trim()) return;
                  try {
                    await api.post(`/mrp/prs/${detail.id}/unapprove`, { reason: reason.trim() });
                    toast.success(`${detail.pr_no} · unapproved · back to OPEN`);
                    setDetail(null); await load();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Unapprove failed");
                  }
                }}
                className="px-3 py-1.5 rounded-sm border border-amber-500/50 text-amber-300 hover:bg-amber-500/10 text-xs font-mono uppercase tracking-widest mr-auto"
              >Unapprove (Admin)</button>
            )}
            {detail && (
              <button data-testid="pr-print-btn" className={btnGhost} onClick={() => openPdf(detail)}>
                <PrinterIcon size={12} weight="bold" />Print PDF
              </button>
            )}
            {detail?.status === "OPEN" && !editMode && (
              <button data-testid="pr-edit-btn" className={btnGhost} onClick={startEdit}>
                <PencilSimpleIcon size={12} weight="bold" />Edit
              </button>
            )}
            {editMode && (
              <>
                <button className={btnGhost} onClick={cancelEdit} disabled={savingEdit}>Cancel Edit</button>
                <button data-testid="pr-save-edit" className={btnPrimary} onClick={saveEdit} disabled={savingEdit}>
                  <CheckCircleIcon size={12} weight="bold" />{savingEdit ? "Saving…" : "Save Adjustments"}
                </button>
              </>
            )}
            {detail?.status === "OPEN" && !editMode && (
              <>
                <button
                  data-testid="pr-reject-btn"
                  className={btnDanger}
                  onClick={() => { setRejectReason(""); setRejectOpen(true); }}
                  disabled={acting}
                >
                  <XCircleIcon size={12} weight="bold" />Reject
                </button>
                <button
                  data-testid="pr-approve-btn"
                  className={btnPrimary}
                  onClick={approve}
                  disabled={acting || !canApprove(detail)}
                  title={!canApprove(detail) ? `Requires role: ${detail?.approver_rule?.approver_role}` : ""}
                >
                  <CheckCircleIcon size={12} weight="bold" />Approve
                </button>
              </>
            )}
            {detail?.status === "APPROVED" && (
              <button
                data-testid="pr-convert-btn"
                className={btnPrimary}
                onClick={openConvert}
                disabled={acting}
              >
                <ShoppingCartIcon size={12} weight="bold" />Create Purchase Order
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject reason */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="pr-reject-dialog" className="bg-[var(--bg-1)] border-[var(--border)] max-w-md" description="Purchase requirement action — convert to PO, short-close or view detail.">
          <DialogHeader><DialogTitle className="font-display text-base flex items-center gap-2"><WarningCircleIcon size={16} weight="bold" className="text-red-400" />Reject {detail?.pr_no}</DialogTitle></DialogHeader>
          <div className="space-y-2 py-1">
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Reason (optional)</label>
            <textarea
              data-testid="pr-reject-reason"
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Why is this PR being rejected?"
              rows={4}
              className="w-full bg-[var(--bg-0)] border border-[var(--border)] rounded-sm px-3 py-2 text-xs focus:outline-none focus:border-[var(--accent)]"
            />
          </div>
          <DialogFooter>
            <button className={btnGhost} onClick={() => setRejectOpen(false)} disabled={acting}>Cancel</button>
            <button data-testid="pr-reject-confirm" className={btnDanger} onClick={reject} disabled={acting}>
              <XCircleIcon size={12} weight="bold" />Reject PR
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert-to-PO preview */}
      <Dialog open={convertOpen} onOpenChange={(o) => { if (!o) { setConvertOpen(false); setConvertPreview(null); } }}>
        <DialogContent data-testid="pr-convert-dialog" className="bg-[var(--bg-1)] border-[var(--border)] max-w-3xl max-h-[90vh] overflow-y-auto" description="Purchase requirement action — convert to PO, short-close or view detail.">
          <DialogHeader>
            <DialogTitle className="font-display text-base flex items-center gap-2">
              <ShoppingCartIcon size={16} weight="bold" className="text-[var(--accent)]" />
              Create Purchase Orders from {detail?.pr_no}
            </DialogTitle>
          </DialogHeader>
          {convertPreview && (
            <div className="space-y-3">
              <div className="text-xs text-zinc-400">
                Lines are grouped by each raw material's <b>default supplier</b> (set on the RM Master).
                One PO will be created per supplier — <b className="text-[var(--accent)]">{convertPreview.po_count}</b> in total.
              </div>

              {(convertPreview.orphan_lines || []).length > 0 && (
                <div data-testid="pr-convert-orphan-rm" className="flex items-start gap-2 text-[11px] text-red-300 bg-red-500/10 border border-red-500/40 rounded-sm px-3 py-2">
                  <WarningCircleIcon size={14} weight="bold" className="mt-0.5" />
                  <div>
                    <div className="font-mono uppercase tracking-widest text-[10px] mb-1">Blocked — {convertPreview.orphan_lines.length} line(s) reference RM codes missing from RM Master:</div>
                    <div className="flex flex-wrap gap-1">
                      {convertPreview.orphan_lines.map((l, i) => (
                        <span key={i} className="font-mono text-[10px] px-1.5 py-0.5 rounded-sm bg-red-500/20 border border-red-500/40">{l.rm_code || l.rm_id}</span>
                      ))}
                    </div>
                    <div className="mt-1 text-zinc-300">Add these Raw Materials to the RM Master (with a default supplier) and retry.</div>
                  </div>
                </div>
              )}

              {(convertPreview.no_supplier_lines || []).length > 0 && (
                <div data-testid="pr-convert-missing-supplier" className="flex items-start gap-2 text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded-sm px-3 py-2">
                  <WarningCircleIcon size={14} weight="bold" className="mt-0.5" />
                  <div>
                    <div className="font-mono uppercase tracking-widest text-[10px] mb-1">Blocked — {convertPreview.no_supplier_lines.length} line(s) have no default supplier on RM Master:</div>
                    <div className="flex flex-wrap gap-1">
                      {convertPreview.no_supplier_lines.map((l, i) => (
                        <span key={i} className="font-mono text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-500/20 border border-amber-500/40">{l.rm_code || l.rm_id}</span>
                      ))}
                    </div>
                    <div className="mt-1 text-zinc-300">Set a default supplier on each RM before converting.</div>
                  </div>
                </div>
              )}

              {(convertPreview.groups || []).map((g, gi) => (
                <div key={g.supplier_id} data-testid={`pr-convert-group-${gi}`} className="border border-[var(--border)] rounded-sm">
                  <div className="px-3 py-2 border-b border-[var(--border)] flex items-center justify-between bg-[var(--bg-2)]/50">
                    <div className="flex items-center gap-2">
                      <span className="text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/40">PO #{gi + 1}</span>
                      <span className="font-mono text-xs">{g.supplier_name || <span className="text-zinc-500">(no name)</span>}</span>
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{g.line_count} lines · ₹{fmt(g.total_value)}</div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-[var(--bg-2)]/30 text-zinc-500 uppercase tracking-widest text-[10px]">
                        <tr>
                          <th className="px-3 py-1.5 text-left">RM Code</th>
                          <th className="px-3 py-1.5 text-left">Description</th>
                          <th className="px-3 py-1.5 text-right w-20">Qty</th>
                          <th className="px-3 py-1.5 text-center w-14">UoM</th>
                          <th className="px-3 py-1.5 text-right w-24">Rate</th>
                          <th className="px-3 py-1.5 text-right w-28">Value (₹)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.lines.map((ln, i) => (
                          <tr key={i} className="border-t border-[var(--border)]">
                            <td className="px-3 py-1.5 font-mono text-xs text-[var(--accent)]">{ln.rm_code}</td>
                            <td className="px-3 py-1.5 text-[11px] text-zinc-400 truncate max-w-[220px]">{ln.rm_name}</td>
                            <td className="px-3 py-1.5 text-right font-mono tabular-nums text-xs">{fmt(ln.qty, 3)}</td>
                            <td className="px-3 py-1.5 text-center text-[10px] text-zinc-500 font-mono">{ln.uom}</td>
                            <td className="px-3 py-1.5 text-right font-mono tabular-nums text-[10px] text-zinc-400">{fmt(ln.unit_cost)}</td>
                            <td className="px-3 py-1.5 text-right font-mono tabular-nums text-xs">{fmt(ln.line_value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <button className={btnGhost} onClick={() => { setConvertOpen(false); setConvertPreview(null); }} disabled={converting}>Cancel</button>
            <button
              data-testid="pr-convert-confirm"
              className={btnPrimary}
              onClick={confirmConvert}
              disabled={converting || !convertPreview || convertPreview.po_count === 0 || (convertPreview.no_supplier_lines || []).length > 0 || (convertPreview.orphan_lines || []).length > 0}
            >
              <ShoppingCartIcon size={12} weight="bold" />
              {converting ? "Creating…" : `Create ${convertPreview?.po_count || 0} PO${(convertPreview?.po_count || 0) !== 1 ? "s" : ""}`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
